import {INotification} from './notification';

//#region INTERFACE INotificationPerStore

/**
 * ### INotificationPerStore
 * Represents aggregated notification data for a specific store.
 *
 * @interface INotificationPerStore
 * @extends Object
 * @description Tracks totals, read/unread counts, general failures, unread failures, and the full notification list per store.
 *
 * @example
 * const storeNotifications: INotificationPerStore = {
 *   total: 10,
 *   read: 6,
 *   unread: 4,
 *   failed: 2,
 *   unreadFailed: 1,
 *   notifications: [...]
 * };
 */
export interface INotificationPerStore extends Object {
  /**
   * The number of notifications that failed in general (regardless of read/unread).
   * @type {number}
   * @example 2
   */
  failed: number;

  /**
   * An array of notifications associated with the store.
   * @type {INotification[]}
   */
  notifications: INotification[];

  /**
   * The number of read notifications for the store.
   * @type {number}
   * @example 6
   */
  read: number;

  /**
   * The total number of notifications for the store.
   * @type {number}
   * @example 10
   */
  total: number;

  /**
   * The number of unread notifications for the store.
   * @type {number}
   * @example 4
   */
  unread: number;

  /**
   * The number of unread notifications that failed to load or process.
   * @type {number}
   * @example 1
   */
  unreadFailed: number;
}


//#endregion

//#region CONST DEFAULT NOTIFICATIONS PER STORE

/**
 * Default notification state for a single store.
 *
 * @constant
 * @type {INotificationPerStore}
 * @default
 */
export const DEFAULT_NOTIFICATIONS_PER_STORE: INotificationPerStore = {
  total: 0,
  read: 0,
  unread: 0,
  failed: 0,
  unreadFailed: 0,
  notifications: [],
};

//#endregion

//#region INTERFACE INotificationTotals

/**
 * ### INotificationTotals
 * Represents aggregated totals for notifications.
 *
 * @interface INotificationTotals
 * @extends Object
 * @description Tracks total, read, unread, and failed unread notification counts.
 *
 * @example
 * const totals: INotificationTotals = {
 *   total: 20,
 *   read: 15,
 *   unread: 5,
 *   failed: 3,
 *   unreadFailed: 1,
 * };
 */
export interface INotificationTotals extends Object {
  /**
   * The number of notifications that failed in general.
   *
   * @type {number}
   * @example 3
   */
  failed: number;

  /**
   * The number of read notifications.
   *
   * @type {number}
   * @example 15
   */
  read: number;

  /**
   * The total number of notifications.
   *
   * @type {number}
   * @example 20
   */
  total: number;

  /**
   * The number of unread notifications.
   *
   * @type {number}
   * @example 5
   */
  unread: number;

  /**
   * The number of unread notifications that failed to load or process.
   *
   * @type {number}
   * @example 1
   */
  unreadFailed: number;
}

//#endregion

//#region CONST DEFAULT_NOTIFICATION_TOTALS

/**
 * Default notification totals with zero counts.
 *
 * @constant
 * @type {INotificationTotals}
 * @default
 */
export const DEFAULT_NOTIFICATION_TOTALS: INotificationTotals = {
  total: 0,
  read: 0,
  unread: 0,
  failed: 0,
  unreadFailed: 0,
};

//#endregion
